import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { getLastIntrospectNodeByOperationAndParam } from '@adornis/introspect/api/get-introspect-node.js';
import { IntrospectViewer } from '@adornis/introspect/client/x-introspect-viewer.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { of } from 'rxjs';
import { setIntercomConversationControlled } from '../../api/clienthelper.js';
import { addCommentToIntercomEntity } from '../../api/comments.js';
import { Comment } from '../../entities/comment.js';
import { IntercomEntity, UserType } from '../../entities/intercom.js';

export const IntercomDetailView = stateful((props: { intercomID: string; onClose: () => void }) => {
  const [comment, setComment] = useState<string>('');
  const [newComment, setNewComment] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showIntrospection, setShowIntrospection] = useState<boolean>(false);

  const intercom = useQuery(
    () =>
      getByID(
        IntercomEntity,
        props.intercomID,
      )({
        _id: 1,
        _class: 1,
        intercomId: 1,
        comment: 1,
        comments: selectionSet(() => Comment, 1),
        lastAuthorType: 1,
        intercomConversationItems: { conversationItemId: 1, body: 1, userType: 1, rating: 1, ratingText: 1 },
        message: 1,
        isCheckedBySupport: 1,
        createdAt: 1,
      }),
    [props.intercomID, refreshTrigger],
  );

  const introspectionNode = useQuery(() => {
    if (!props.intercomID) return of(null);
    return getLastIntrospectNodeByOperationAndParam(
      'handleIntercomMode',
      JSON.stringify({ intercomID: props.intercomID }),
    )({ _id: 1 });
  }, [props.intercomID]);

  useEffect(() => {
    if (!comment && intercom.value?.comment) setComment(intercom.value.comment || '');
  }, [comment, intercom.value?.comment]);

  const handleSetIntercomConversationControlled = async () => {
    await setIntercomConversationControlled(props.intercomID)();
  };

  // Handle comment submission to comments array
  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;

    try {
      setIsSubmitting(true);
      setToastMessage(null);

      await addCommentToIntercomEntity(newComment.trim(), props.intercomID)({});
      setNewComment('');

      // Refresh the data by updating the refresh trigger
      setRefreshTrigger(refreshTrigger + 1);

      // Show success toast
      setToastMessage({ type: 'success', message: 'Comment added successfully!' });

      // Auto-dismiss toast after 3 seconds
      setTimeout(() => {
        setToastMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Failed to add comment:', error);
      setToastMessage({ type: 'error', message: 'Failed to add comment' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render comments
  const renderComments = (comments: Comment[]) => {
    if (!comments || comments.length === 0) {
      return html`<div class="text-sm italic text-base-content/50 p-4 text-center">No comments yet</div>`;
    }

    return html`
      <div class="space-y-3">
        ${comments.map(
          (comment, index) => html`
            <div class="bg-base-200 p-3 rounded-lg">
              <div class="flex justify-between items-start mb-2">
                <div class="text-xs text-base-content/40">
                  ${comment.createdAt ? format(comment.createdAt, 'PPp') : 'Unknown date'}
                </div>
                <div class="text-xs text-base-content/50">#${index + 1}</div>
              </div>
              <div class="text-sm whitespace-pre-wrap">${comment.text}</div>
            </div>
          `,
        )}
      </div>
    `;
  };

  // Show loading state if intercom data is not yet loaded
  if (!intercom.value) {
    return html`
      <div class="card bg-base-100 shadow-xl border border-base-300 h-full">
        <div class="card-body flex items-center justify-center">
          <span class="loading loading-spinner loading-lg text-secondary"></span>
        </div>
      </div>
    `;
  }

  return html`
    <div class="card bg-base-100 shadow-xl border border-base-300 h-full">
      <div class="card-body">
        <div class="flex justify-between items-center">
          <h2 class="card-title text-xl font-bold">Intercom Details</h2>
          <button class="btn btn-sm btn-circle btn-ghost" @click=${props.onClose} title="Close">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-5 h-5"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <p class="text-sm text-info">ID: ${intercom.value._id}</p>
        <div class="divider"></div>

        <div class="space-y-4 overflow-y-auto max-h-[calc(100vh-300px)]">
          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Intercom ID</span>
            </label>
            <div class="bg-base-200 p-3 rounded-lg text-sm">${intercom.value.intercomId || 'N/A'}</div>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Created At</span>
            </label>
            <div class="bg-base-200 p-3 rounded-lg text-sm">
              ${intercom.value.createdAt ? format(intercom.value.createdAt, 'PPpp') : 'N/A'}
            </div>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Author</span>
            </label>
            <div class="bg-base-200 p-3 rounded-lg text-sm">${intercom.value.lastAuthorType?.toString() || 'N/A'}</div>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Message</span>
            </label>
            <div class="bg-base-200 p-3 rounded-lg text-sm whitespace-pre-wrap">${intercom.value.message || 'N/A'}</div>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Intercom Messages</span>
            </label>
            <div class="flex space-x-2 ml-auto">
              ${introspectionNode.value
                ? html`
                    <button class="btn btn-xs btn-outline" @click=${() => setShowIntrospection(!showIntrospection)}>
                      ${showIntrospection ? 'Hide Introspection Answer' : 'Show Introspection Answer'}
                    </button>
                  `
                : nothing}
            </div>
            <div class="space-y-5">
              ${intercom.value.intercomConversationItems?.map(
                (msg, idx) =>
                  html` <div>
                    <div
                      class="p-2 bg-base-200 rounded items-center gap-2"
                      style="
                      width: 70%;
                      float: ${msg.userType === UserType.USER ? 'left' : 'right'};
                      background-color: ${msg.userType === UserType.ADMIN
                        ? '#DFFFE0'
                        : msg.userType === UserType.USER
                          ? '#FFF9D6'
                          : '#E0F7FF'};"
                    >
                      <!--<p class="text-sm font-medium p-2"><strong>${`Index ${idx + 1}`}</strong></p>-->
                      <p class="text-sm font-medium p-2">
                        <strong>ConversationPart-ID: </strong>${msg.conversationItemId || `N/A`}
                      </p>
                      ${msg.userType === UserType.BOT && msg.rating
                        ? `
                      <p class="text-sm font-medium p-2">
                      <strong>Rating: </strong>${msg.rating}
                    </p>`
                        : ''}

                      <p class="text-sm font-medium p-2">
                        <strong>UserType: </strong>${msg.userType === UserType.ADMIN
                          ? 'ADMIN'
                          : msg.userType === UserType.USER
                            ? 'USER'
                            : 'BOT'}
                      </p>
                      <p class="text-sm font-medium p-2">
                        ${idx > 0 ? msg.body || 'N/A' : msg.body || intercom.value.message || 'N/A'}
                      </p>
                    </div>
                  </div>`,
              )}
            </div>
          </div>
          <br /><br />

          <! -- Handle set support checked-->
          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Already Checked</span>
              <span class="label-text-alt">Is checked by support</span>
            </label>
            <div class="bg-base-200 rounded-lg">
              <input
                type="checkbox"
                class="bg-base-200 text-sm resize-none"
                .checked=${intercom.value.isCheckedBySupport}
                @click=${(e: Event) => handleSetIntercomConversationControlled()}
              />
            </div>
          </div>

          <!-- Comments Section -->
          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Comments</span>
              <span class="label-text-alt">${intercom.value.comments?.length || 0} comments</span>
            </label>
            <div class="bg-base-200 rounded-lg">${renderComments(intercom.value.comments)}</div>
          </div>

          <!-- Add Comment Form -->
          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Add Comment</span>
            </label>
            <div class="relative">
              <textarea
                class="textarea textarea-bordered w-full h-24 bg-base-200 text-sm resize-none"
                placeholder="Add a comment..."
                .value=${newComment}
                @input=${(e: InputEvent) => setNewComment((e.target as HTMLTextAreaElement).value)}
              ></textarea>
              <div class="absolute bottom-3 right-3">
                <button
                  class="btn btn-sm btn-primary"
                  @click=${handleSubmitComment}
                  ?disabled=${!newComment.trim() || isSubmitting}
                >
                  ${isSubmitting
                    ? html`<span class="loading loading-spinner loading-xs mr-1"></span> Saving...`
                    : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    ${showIntrospection
      ? html`
          <div class="modal modal-open">
            <div class="modal-box w-full h-full p-0 relative max-w-full rounded-none">
              ${IntrospectViewer({ nodeID: introspectionNode.value?._id })}
              <button
                class="btn btn-sm btn-circle absolute right-2 top-2"
                @click=${() => setShowIntrospection(false)}
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>
        `
      : nothing}
    ${toastMessage
      ? html`
          <div class="toast toast-end">
            <div class="alert ${toastMessage.type === 'success' ? 'alert-success' : 'alert-error'} shadow-lg">
              <div>
                ${toastMessage.type === 'success'
                  ? html`
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    `
                  : html`
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    `}
                <span>${toastMessage.message}</span>
              </div>
            </div>
          </div>
        `
      : ''}
  `;
});
